import api, { isAPIError, isHTTPError } from 'containers/api'
import { useState } from 'react'
import { selectToken } from 'containers/authentication/authenticationSlice'
import { useSelector } from 'react-redux'
import { Bookmark, NavigationBookmark } from 'types'

export enum GeneratingBookmarkStatus {
  'idle' = 'idle',
  'loading' = 'loading',
  'success' = 'success',
  'error' = 'error',
}

type BookmarkError = {
  message: string
  type: string
  tip?: string
}

const useBookmarks = (organizationId) => {
  const token = useSelector(selectToken)
  const [navigationBookmarks, setNavigationBookmarks] = useState<NavigationBookmark[]>([])
  const [fetchedBookmarks, setFetchedBookmarks] = useState<Bookmark[]>([])
  const [bookmarkError, setBookmarkError] = useState<BookmarkError | null>(null)
  const [fetchingBookmark, setFetchingBookmark] = useState(false)
  const [generatingBookmarkStatus, setGeneratingBookmarkStatus] =
    useState<GeneratingBookmarkStatus>(GeneratingBookmarkStatus.idle)
  const [generationResponse, setGenerationResponse] = useState<string | null>(null)

  const getBookmarks = async (reportSlug: string) => {
    try {
      setFetchingBookmark(true)
      const bookmarks = await api.getReportBookmarks({
        organizationId,
        reportSlug,
        token,
      })
      setFetchedBookmarks(bookmarks)
      setFetchingBookmark(false)
      return bookmarks
    } catch (error) {
      setFetchingBookmark(false)
      if (isAPIError(error)) {
        setBookmarkError({
          message: 'Data not yet available. Please try again later.',
          type: error.response.data.detail.type,
        })
      } else {
        console.error('error loading embed report bookmarks', error)
        setBookmarkError({
          message: 'Failed to load bookmarks. Please try again later.',
          type: JSON.stringify(error),
        })
      }
    }
  }

  const getNavigationBookmarks = async (reportSlug: string) => {
    try {
      setFetchingBookmark(true)
      const bookmarks = await api.getNavigationBookmarks({
        organizationId,
        reportSlug,
        token,
      })
      setNavigationBookmarks(bookmarks)
      setFetchingBookmark(false)
      return bookmarks
    } catch (error) {
      setFetchingBookmark(false)
      if (isAPIError(error)) {
        setBookmarkError({
          message: 'Data not yet available. Please try again later.',
          type: error.response.data.detail.type,
        })
      } else {
        console.error('error loading navigation bookmarks', error)
        setBookmarkError({
          message: 'Failed to load navigation bookmarks. Please try again later.',
          type: JSON.stringify(error),
        })
      }
    }
  }

  const batchCreateBookmarks = async (
    reportSlug: string,
    bookmarks: Bookmark[]
  ) => {
    try {
      const response = await api.createBatchReportBookmarks({
        organizationId,
        reportSlug,
        bookmarks,
        token,
      })
      if (response.success) {
        setFetchedBookmarks(response.bookmarks)
      }
      return response
    } catch (error) {
      if (isHTTPError(error) && error.response.status === 404) {
        setBookmarkError({
          message: 'Could not create bookmark',
          // @ts-ignore
          type: error.response.data.type,
        })
      }
      if (isHTTPError(error) && 'detail' in error.response.data) {
        setBookmarkError({
          message: 'Error creating bookmark',
          type: error.response.data.detail.type,
        })
      }
      return null
    }
  }

  const deleteBookmark = async (reportSlug: string, bookmarkId: number) => {
    try {
      await api.deleteReportBookmark({
        organizationId,
        reportSlug,
        bookmarkId,
        token,
      })
      setFetchedBookmarks((prevBookmarks) =>
        prevBookmarks.filter((bookmark) => bookmark.id !== bookmarkId)
      )
    } catch (error) {
      if (isHTTPError(error) && error.response.status === 404) {
        setBookmarkError({
          message: 'Could not delete bookmark',
          // @ts-ignore
          type: error.response.data.type,
        })
      }
      if (isHTTPError(error) && 'detail' in error.response.data) {
        setBookmarkError({
          message: 'Error deleting bookmark',
          type: error.response.data.detail.type,
        })
      }
      console.error('Error deleting bookmark:', error)
    }
  }
  const updateBookmark = async (
    reportSlug: string,
    bookmarkId: number,
    updatedBookmark: Bookmark
  ) => {
    try {
      const response = await api.updateReportBookmark({
        organizationId,
        reportSlug,
        bookmarkId,
        updatedBookmark,
        token,
      })
      setFetchedBookmarks((prevBookmarks) =>
        prevBookmarks.map((bookmark) => {
          if (bookmark.id === updatedBookmark.id) {
            return updatedBookmark
          }
          return bookmark
        })
      )
      return response
    } catch (error) {
      if (isHTTPError(error) && error.response.status === 404) {
        setBookmarkError({
          message: 'Could not update bookmark',
          // @ts-ignore
          type: error.response.data.type,
        })
      }
      if (isHTTPError(error) && 'detail' in error.response.data) {
        setBookmarkError({
          message: 'Error updating bookmark',
          type: error.response.data.detail.type,
        })
      }
      console.error('Error updating bookmark:', error)
    }
  }

  const generateBookmark = async (reportSlug: string, prompt: string) => {
    try {
      setGeneratingBookmarkStatus(GeneratingBookmarkStatus.loading)
      const response = await api.generateBookmark({
        organizationId,
        reportSlug,
        prompt,
        token,
      })
      if (response.success) {
        setGeneratingBookmarkStatus(GeneratingBookmarkStatus.success)
        setFetchedBookmarks((prevBookmarks) => [
          ...prevBookmarks,
          response.bookmark,
        ])
        if (response.text_response) {
          setGenerationResponse(response.text_response)
        }
      } else {
        setGeneratingBookmarkStatus(GeneratingBookmarkStatus.error)
        setBookmarkError({
          message: 'Error generating bookmark',
          type: JSON.stringify(response),
        })
      }
      return response
    } catch (error) {
      setGeneratingBookmarkStatus(GeneratingBookmarkStatus.error)
      if (isHTTPError(error) && error.response.status === 404) {
        setBookmarkError({
          message: 'Organization has no workspace setup',
          // @ts-ignore
          type: error.response.data.detail.type,
        })
      }
      if (isHTTPError(error) && 'detail' in error.response.data) {
        let tip: string | undefined;
        if (error.response.data.detail.type == "unauthorized") {
          tip = "Try refreshing the page or logging in again."
        } else if (error.response.data.detail.type == "invalid_prompt") {
          tip = "Try rephrasing your prompt."
        }
        setBookmarkError({
          message: 'Error generating bookmark',
          type: error.response.data.detail.type,
          tip: tip,
        })
      }
      console.error('Error generating bookmark:', error)
    }
  }

  return {
    navigationBookmarks,
    fetchedBookmarks,
    setFetchedBookmarks,
    fetchingBookmark,
    generatingBookmarkStatus,
    setGeneratingBookmarkStatus,
    generationResponse,
    setGenerationResponse,
    bookmarkError,
    setBookmarkError,
    getBookmarks,
    getNavigationBookmarks,
    batchCreateBookmarks,
    deleteBookmark,
    updateBookmark,
    generateBookmark,
  }
}

export default useBookmarks
